"use client";
import Link from "next/link";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { CatalogListResponse } from "@/lib/billing/types";
import { useI18n } from "@/lib/i18n/I18nProvider";
import type { Dictionary } from "@/lib/i18n/dictionaries";

type OffersCopy = Dictionary["offers"];

/**
 * The catalog returns its billing unit as English text ("per month"). Known
 * intervals are shown in the reader's language; anything else is passed
 * through unchanged.
 */
function billingUnit(
  t: OffersCopy,
  unit: string | null | undefined,
  interval: string | null | undefined,
) {
  const match = /^per (hour|day|month|year)$/i.exec(unit?.trim() ?? "");
  const key = (match?.[1] ?? (unit ? null : interval))?.toLowerCase();
  if (key && key in t.intervals) return t.intervals[key as keyof OffersCopy["intervals"]];
  if (unit) return unit;
  return interval ? `${t.per} ${interval}` : "";
}

export function CatalogOffers({
  limit = 3,
  filterable = false,
}: {
  limit?: number;
  filterable?: boolean;
}) {
  const t = useI18n().t.offers;
  const [family, setFamily] = useState("vm,container,function");
  return (
    <div>
      {filterable && (
        <div
          className="ex-offer-filters"
          role="group"
          aria-label={t.filtersLabel}
        >
          {(
            [
              ["vm,container,function", t.filters.all],
              ["vm", t.filters.vm],
              ["container", t.filters.container],
              ["function", t.filters.function],
            ] as const
          ).map(([value, label]) => (
            <button
              type="button"
              key={value}
              aria-pressed={family === value}
              onClick={() => setFamily(value)}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      <OfferResults limit={limit} family={family} />
    </div>
  );
}

function OfferResults({ limit, family }: { limit: number; family: string }) {
  const { locale, t: dict } = useI18n();
  const t = dict.offers;
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ["marketing", "published-offers", limit, family],
    queryFn: () =>
      api<CatalogListResponse>(
        `/api/v1/catalog/products?limit=${limit}&sort=name&family=${family}`,
      ),
    retry: false,
  });
  if (isPending)
    return (
      <div className="ec-offer-state" role="status">
        {t.loading}
      </div>
    );
  if (isError)
    return (
      <div className="ec-offer-state" role="status">
        <h3>{t.errorTitle}</h3>
        <p>{t.errorBody}</p>
        <button
          className="ec-button ec-button-outline"
          onClick={() => void refetch()}
        >
          {t.retry}
        </button>
      </div>
    );
  if (!data?.products.length)
    return (
      <div className="ec-offer-state">
        <h3>{t.emptyTitle}</h3>
        <p>{t.emptyBody}</p>
        <Link className="ec-text-link" href="/contact">
          {t.emptyLink}
        </Link>
      </div>
    );
  return (
    <div className="ec-offers">
      {data.products.map((p) => (
        <article className="ec-offer" key={p.slug}>
          <span className="ec-eyebrow">
            {t.families[p.family as keyof OffersCopy["families"]] ?? p.family}
          </span>
          <h3>{p.name}</h3>
          <div className="ec-offer-price">
            {p.from_price ? (
              <>
                <span>{t.from}</span>
                <strong>
                  {new Intl.NumberFormat(locale, {
                    style: "currency",
                    currency: p.from_price.currency,
                  }).format(p.from_price.amount_minor / 100)}
                </strong>
                <span>
                  {billingUnit(t, p.billing_unit, p.from_price.interval)}
                </span>
              </>
            ) : (
              <p>{t.seeOffer}</p>
            )}
          </div>
          {p.key_resources && (
            <ul className="ec-offer-resources">
              {p.key_resources.vcpu != null && (
                <li>{p.key_resources.vcpu} vCPU</li>
              )}
              {p.key_resources.memory_mb != null && (
                <li>
                  {p.key_resources.memory_mb} {t.memory}
                </li>
              )}
              {p.key_resources.disk_gb != null && (
                <li>
                  {p.key_resources.disk_gb} {t.storage}
                </li>
              )}
            </ul>
          )}
          <Link
            className="ec-button ec-button-outline"
            href={`/store/p/${p.slug}`}
          >
            {t.review} <span aria-hidden="true">↗</span>
          </Link>
        </article>
      ))}
    </div>
  );
}
